import { useState, useEffect, Fragment, useCallback, useRef } from "react";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import { getRegionForGoogleMaps } from "../../../utils/transformers.utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { getQueryFn } from "../../../utils/api/queryFunction";
import { isInternetExplorer, get_browser } from "../../../utils";
import { addDataLayerEvent } from "../../../utils/data.layer.utils/data.layer.util";
import { pickDataForFindADealerGTM } from "../../../utils/data.layer.utils/fad.data.layer.util";

const { publicRuntimeConfig } = getConfig();

import DealerContactCard from "../../Organisms/Cards/DealerContactCard";

const LazyLoad = dynamic(() => import("../../Shared/Image/LazyLoad"));

let dealerListRef = [];

const FADComponent = (props) => {
	const { pageData, category } = props;
	const [showSearch, setShowSearch] = useState(false);
	const [googleMap, setGoogleMap] = useState(null);
	const [locMarker, setLocMarker] = useState(null);

	const [locLat, setLat] = useState(null);
	const [locLng, setLng] = useState(null);

	const [autoComplete, setAutoComplete] = useState(null);
	const [dealers, setDealers] = useState([]);
	const [selectedDealer, setSelectedDealer] = useState("");
	const [canRenderUCL, setCanRenderUCL] = useState(true);
	const [mapMountState, setMapMountState] = useState(false);
	const [urlQuery] = useState(props.query ? props.query : {});

	const [width, setWidth] = useState("");
	const updateWidth = () => {
		if (typeof window !== "undefined") {
			setWidth(window.innerWidth);
		}
	};

	function mapMountCallback() {
		const ref = useRef(null);
		const setRef = useCallback((node) => {
			if (ref.current) {
				// Make sure to cleanup any events/references added to the last instance
			}

			if (node) {
				setMapMountState(true);
			}

			ref.current = node;
		}, []);

		return [setRef];
	}

	useEffect(() => {
		if (!width) {
			updateWidth();
		}
		window.addEventListener("resize", updateWidth);
		return () => window.removeEventListener("resize", updateWidth);
	}, []);

	useEffect(() => {
		if (pageData.dealerCount && pageData.dealerCount < 4) {
			const lat =
				pageData.dealerCount === 1 &&
				pageData.defaultDealer?.address?.lat
					? parseFloat(pageData.defaultDealer.address.lat)
					: parseFloat(publicRuntimeConfig.googleMapsLat);

			const lng =
				pageData.dealerCount === 1 &&
				pageData.defaultDealer?.address?.lng
					? parseFloat(pageData.defaultDealer.address.lng)
					: parseFloat(publicRuntimeConfig.googleMapsLng);

			setLatLngValues(lat, lng);
			setShowSearch(true);
		}

		const timeoutId = setTimeout(createMap, 500);

		return () => clearTimeout(timeoutId);
	}, [pageData]);

	useEffect(() => {
		if (autoComplete === null && locMarker !== null) {
			if (showSearch) {
				setLatLngValues(
					locMarker.position.lat(),
					locMarker.position.lng()
				);
			}
			createAutoComplete();
		}
	}, [locMarker]);

	useEffect(() => {
		const fetchDealersIfLocationChanged = async () => {
			await getDealersNearLocation();
			const pos = new google.maps.LatLng(locLat, locLng);
			setGoolgeMapsLocationMarker(pos, 10);
		};

		if (locLat && locLng) {
			fetchDealersIfLocationChanged();
		}
	}, [locLat, locLng]);

	useEffect(() => {
		if (mapMountState) {
			setCanRenderUCL(
				!isInternetExplorer(get_browser(window.navigator.userAgent))
			);
		}
	}, [mapMountState]);

	useEffect(() => {
		if (autoComplete !== null) {
			setAutoCompleteListeners();
		}
	}, [autoComplete]);

	const setLatLngValues = async (lat, lng) => {
		if (locLat === lat && locLng === lng) return;
		setLat(lat);
		setLng(lng);
	};

	const createMap = () => {
		const mapElement = document.getElementById("map");
		if (!mapElement) return;

		console.log(
			"createMap - locLat",
			locLat,
			parseFloat(publicRuntimeConfig.googleMapsLat)
		);
		console.log(
			"createMap - locLng",
			locLng,
			parseFloat(publicRuntimeConfig.googleMapsLng)
		);

		const lat = locLat ?? parseFloat(publicRuntimeConfig.googleMapsLat);
		const lng = locLng ?? parseFloat(publicRuntimeConfig.googleMapsLng);

		const map = new google.maps.Map(mapElement, {
			center: { lat, lng },
			zoom: 10,
			disableDefaultUI: true,
		});

		setGoogleMap(map);

		const myMarker = new google.maps.Marker({
			map,
			title: "My Location",
			icon: "/static/img/maps/pin.png",
			position: new google.maps.LatLng(lat, lng),
			anchorPoint: new google.maps.Point(0, -29),
		});

		setLocMarker(myMarker);
	};

	const setAutoCompleteListeners = () => {
		autoComplete.addListener("place_changed", function () {
			onPlaceChanged();
		});
	};

	let regionSplit = getRegionForGoogleMaps();

	const createAutoComplete = () => {
		if (!autoComplete) {
			const temp = new google.maps.places.Autocomplete(
				document.getElementById("autoCompleteSearch"),
				{}
			);
			temp.setComponentRestrictions({
				country: regionSplit,
			});
			setAutoComplete(temp);
		}
	};

	const setGoolgeMapsLocationMarker = async (loc, zoom = 13) => {
		if (googleMap != null || googleMap != undefined) {
			googleMap.setCenter(loc);
			googleMap.setZoom(parseInt(zoom));

			if (!showSearch) {
				locMarker.setPosition(loc);
				locMarker.setVisible(true);
				setLatLngValues(
					locMarker.position.lat(),
					locMarker.position.lng()
				);
				setShowSearch(true);
			}
		}
	};

	const onPlaceChanged = async () => {
		locMarker.setVisible(false);

		var place = autoComplete.getPlace();
		if (!place.geometry) {
			// User entered the name of a Place that was not suggested and
			// pressed the Enter key, or the Place Details request failed.
			window.alert(
				"No details available for input: '" + place.name + "'"
			);
			return;
		}

		addDataLayerEvent(
			"FindADealerSearchLocation",
			pickDataForFindADealerGTM({
				category: category,
				searchTerm: place.formatted_address,
				place: {
					name: place.name,
					address: place.address_components,
				},
			})
		);

		setGoolgeMapsLocationMarker(place.geometry.location);
	};

	const getDealersNearLocation = async () => {
		try {
			const dealers = await getQueryFn(
				`dealers?category=${category}&lng=${locLng}&lat=${locLat}&spec=${pageData.dealerSpec}`
			);
	
			if (!dealers?.data) throw new Error("No dealer data found");
	
			const dealerList = dealers.data;
	
			// Function to create a Google Maps marker
			const createMarker = (dealer) => {
				const marker = new google.maps.Marker({
					map: googleMap,
					title: dealer.title,
					icon: "/static/img/maps/location.png",
					position: new google.maps.LatLng({
						lat: Number(dealer.address?.lat),
						lng: Number(dealer.address?.lng),
					}),
					slug: dealer.slug,
				});
	
				google.maps.event.addListener(marker, "click", () => {
					googleMap.setCenter(marker.position);
					googleMap.setZoom(17);
	
					const dealerElement = dealerListRef[marker.slug];
					const scrollContainer = document.querySelector(".dealersHolder");
	
					if (dealerElement && scrollContainer) {
						const scrollPosition =
							dealerElement.offsetTop - dealerElement.offsetHeight - 15;
						scrollContainer.scrollTo({ top: scrollPosition, behavior: "smooth" });
					}
	
					setSelectedDealer(marker.slug);
				});
			};
	
			// Create markers for each dealer
			dealerList.forEach(createMarker);
	
			// Update state
			setDealers(dealerList);
	
			// Send data layer event
			addDataLayerEvent(
				"FindADealerUpdateList",
				pickDataForFindADealerGTM({
					category,
					dealers: dealerList,
				})
			);
	
			return dealerList;
		} catch (err) {
			console.error("Error fetching dealers:", err);
		}
	};
	

	useEffect(() => {
		if (urlQuery.lat && urlQuery.lng && googleMap) {
			setGoolgeMapsLocationMarker({
				lat: parseFloat(urlQuery.lat),
				lng: parseFloat(urlQuery.lng),
			});
		}
	}, [googleMap, urlQuery]);

	const getCurrentLocation = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				function (position) {
					setGoolgeMapsLocationMarker({
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					});
					addDataLayerEvent(
						"FindADealerCurrentLocation",
						pickDataForFindADealerGTM({
							category: category,
							location: position,
						})
					);
				},
				function () {}
			);
		}
	};

	const zoomToDealer = (dealer) => {
		const pos = new google.maps.LatLng(
			dealer.address.lat,
			dealer.address.lng
		);
		setGoolgeMapsLocationMarker(pos, 17);
		setSelectedDealer(dealer.slug);
	};

	const isMobile = width < 767;

	const banner = {
		full: {
			height: "100vh",
		},
		small: {
			height: "25vh",
		},
	};

	const maps = {
		enter: {
			height: "75vh",
		},
		exit: {
			height: "0",
		},
		enterMobile: {
			height: "auto",
		},
		exitMobile: {
			height: "0",
		},
	};

	const [map] = mapMountCallback();

	return (
		<Fragment>
			<div
				className={
					showSearch
						? "showLocationVisible findADealer"
						: "findADealer"
				}
			>
				<motion.div
					className={`banner ${showSearch ? "small" : "full"}`}
					animate={showSearch ? "small" : "full"}
					initial="full"
					variants={banner}
				>
					<div className="overlay"></div>
					{pageData.banner && pageData.banner.image && (
						<LazyLoad
							image={pageData.banner.image}
							alt="pageData.banner.title"
						/>
					)}
					<h1 className="bannerTitle">{pageData.banner.title}</h1>
					<div className="location">
						{canRenderUCL && (
							<Fragment>
								<button
									className="currentLocation"
									onClick={() => {
										getCurrentLocation();
									}}
								>
									<FontAwesomeIcon
										icon={["far", "location"]}
									/>
									Use My Current Location
								</button>
								<h2>OR</h2>
							</Fragment>
						)}

						<input
							className="browser-default searchLocation"
							type="text"
							placeholder="Enter suburb, city or province"
							id="autoCompleteSearch"
						/>
					</div>
				</motion.div>
				<motion.div
					className="results"
					animate={
						isMobile
							? showSearch
								? "enterMobile"
								: "exitMobile"
							: showSearch
							? "enter"
							: "exit"
					}
					initial={isMobile ? "exitMobile" : "exit"}
					variants={maps}
				>
					<div className="row">
						<div
							className="col s12 m4 l4 dealersHolder"
							ref={(ref) => {
								dealerListRef["dealersHolder"] = ref;
							}}
						>
							<h4>Nearest Dealers:</h4>
							{/* Add loading screen when dealers are getting fetched */}
							{dealers.map((dealer) => {
								return (
									<DealerContactCard
										key={dealer.slug}
										category={category}
										dealer={dealer}
										zoomToDealer={zoomToDealer}
										dealerListRef={dealerListRef}
										selectedDealer={selectedDealer}
									/>
								);
							})}
						</div>

						<div className="col s12 m8 l8 mapHolder">
							<div ref={map} id="map"></div>
						</div>
					</div>
				</motion.div>
			</div>
		</Fragment>
	);
};

export default FADComponent;
